(function () {
  const META_SELECTOR = 'meta[name="theme-color"]';
  const SECTION_SELECTOR = '[data-theme-color-section]';
  const TOP_BAR_SELECTOR = '[data-theme-color-role="top-bar"]';
  const MAIN_SELECTOR = '#xo-main-content';
  const HEADER_SELECTOR = '.header-humm';
  const ANNOUNCEMENT_SELECTOR = '#xo-announcement-bar';
  const TOP_EDGE_TOLERANCE = 8;
  const DEFAULT_COLOR = '#ffffff';

  let currentColor = '';
  let frameId = null;

  function ensureMeta() {
    let meta = document.querySelector(META_SELECTOR);

    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }

    return meta;
  }

  function isTransparent(color) {
    return !color || color === 'transparent' || /rgba?\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0\s*\)/i.test(color);
  }

  function toHexChannel(value) {
    const channel = Math.max(0, Math.min(255, Number.parseInt(value, 10) || 0));
    return channel.toString(16).padStart(2, '0');
  }

  function normalizeColor(value) {
    const color = value.trim();

    if (!color || isTransparent(color)) return '';

    const hex = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (hex) {
      const raw = hex[1];
      return raw.length === 3
        ? `#${raw.split('').map((char) => char + char).join('')}`.toLowerCase()
        : `#${raw}`.toLowerCase();
    }

    const rgb = color.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/i);
    if (rgb) {
      const alpha = rgb[4] === undefined ? 1 : Number.parseFloat(rgb[4]);
      if (alpha <= 0) return '';
      return `#${toHexChannel(rgb[1])}${toHexChannel(rgb[2])}${toHexChannel(rgb[3])}`;
    }

    const channels = color.match(/^\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*$/);
    if (channels) {
      return `#${toHexChannel(channels[1])}${toHexChannel(channels[2])}${toHexChannel(channels[3])}`;
    }

    return color;
  }

  function readColor(element, fallbackToWhite) {
    if (!element) return fallbackToWhite ? DEFAULT_COLOR : '';

    const styles = window.getComputedStyle(element);
    const colorBackground = styles.getPropertyValue('--color-background').trim();
    const candidates = [
      styles.backgroundColor,
      styles.getPropertyValue('--bg-color'),
      colorBackground ? `rgb(${colorBackground})` : '',
      colorBackground,
    ];

    for (const candidate of candidates) {
      const color = normalizeColor(candidate);
      if (color) return color;
    }

    return fallbackToWhite ? DEFAULT_COLOR : '';
  }

  function isVisible(element) {
    if (!element || element.classList.contains('is-dismissed')) return false;

    const styles = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();

    return (
      styles.display !== 'none' &&
      styles.visibility !== 'hidden' &&
      rect.width > 0 &&
      rect.height > 0
    );
  }

  function getBottomEdge(selector) {
    const element = document.querySelector(selector);
    return isVisible(element) ? element.getBoundingClientRect().bottom : 0;
  }

  function getAnchorY() {
    const announcementBottom = getBottomEdge(ANNOUNCEMENT_SELECTOR);
    const headerBottom = getBottomEdge(HEADER_SELECTOR);

    return Math.max(announcementBottom, headerBottom, 0) + 1;
  }

  function isTopBarActive(element) {
    if (!isVisible(element)) return false;

    const rect = element.getBoundingClientRect();

    return rect.bottom > 0 && rect.top <= TOP_EDGE_TOLERANCE;
  }

  function findActiveTopBar() {
    return Array.from(document.querySelectorAll(TOP_BAR_SELECTOR)).find(isTopBarActive);
  }

  function getThemeSection(sectionWrapper) {
    if (!sectionWrapper) return null;
    if (sectionWrapper.matches(SECTION_SELECTOR)) return sectionWrapper;

    return sectionWrapper.querySelector(SECTION_SELECTOR);
  }

  function findActiveContentSection(anchorY) {
    const main = document.querySelector(MAIN_SELECTOR);
    if (!main) return null;

    let lastSectionAboveAnchor = null;

    for (const child of Array.from(main.children)) {
      if (!isVisible(child)) continue;

      const themeSection = getThemeSection(child);
      if (!themeSection || !isVisible(themeSection)) continue;
      if (themeSection.dataset.themeColorRole === 'top-bar') continue;

      const rect = themeSection.getBoundingClientRect();

      if (rect.top <= anchorY && rect.bottom > anchorY) {
        return themeSection;
      }

      if (rect.top <= anchorY) {
        lastSectionAboveAnchor = themeSection;
      }
    }

    return lastSectionAboveAnchor;
  }

  function resolveThemeColor() {
    const topBar = findActiveTopBar();
    if (topBar) {
      const topBarColor = readColor(topBar, true);
      if (topBarColor) return topBarColor;
    }

    const activeContentSection = findActiveContentSection(getAnchorY());
    if (activeContentSection) {
      const contentColor = readColor(activeContentSection, true);
      if (contentColor) return contentColor;
    }

    return DEFAULT_COLOR;
  }

  function updateThemeColor() {
    frameId = null;

    const nextColor = resolveThemeColor() || DEFAULT_COLOR;
    if (nextColor === currentColor) return;

    ensureMeta().setAttribute('content', nextColor);
    currentColor = nextColor;
  }

  function scheduleUpdate() {
    if (frameId) return;
    frameId = window.requestAnimationFrame(updateThemeColor);
  }

  function initThemeColor() {
    scheduleUpdate();

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('load', scheduleUpdate);
    document.addEventListener('shopify:section:load', scheduleUpdate);
    document.addEventListener('shopify:section:unload', scheduleUpdate);
    document.addEventListener('shopify:section:select', scheduleUpdate);

    if ('MutationObserver' in window) {
      const observer = new MutationObserver(scheduleUpdate);
      observer.observe(document.body, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['class', 'style', 'hidden', 'data-theme-color-role'],
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initThemeColor, { once: true });
  } else {
    initThemeColor();
  }
})();
